import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import {
  Box,
  Chip,
  Divider,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { DIPLOMES } from "../DiplomeRequisPage";

const DESC_DETAIL = [
  { value: "Cons_int", label: "Consultation interne" },
  { value: "Cons_ext", label: "Consultation externe" },
  { value: "Cons_indiv", label: "Consultation individuelle" },
  { value: "EMS", label: "EMS" },
];

const formatDate = (date) => (date ? String(date).slice(0, 10) : "");

const formatPublicationDuration = (start, end) => {
  if (!start || !end) return "Non spécifié";
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (
    Number.isNaN(startDate.getTime()) ||
    Number.isNaN(endDate.getTime()) ||
    endDate < startDate
  ) {
    return "Non spécifié";
  }

  let years = endDate.getFullYear() - startDate.getFullYear();
  let months = endDate.getMonth() - startDate.getMonth();
  let days = endDate.getDate() - startDate.getDate();

  if (days < 0) {
    months -= 1;
    const previousMonthLastDay = new Date(
      endDate.getFullYear(),
      endDate.getMonth(),
      0,
    ).getDate();
    days += previousMonthLastDay;
  }

  if (months < 0) {
    years -= 1;
    months += 12;
  }

  const parts = [];
  if (years > 0) parts.push(`${years} an${years > 1 ? "s" : ""}`);
  if (months > 0) parts.push(`${months} mois`);
  if (days > 0) parts.push(`${days} jour${days > 1 ? "s" : ""}`);

  return parts.length > 0 ? parts.join(" ") : "0 jour";
};

const InfoCard = ({ icon, label, value }) => (
  <Paper
    variant="outlined"
    sx={{
      height: "100%",
      p: 1.75,
      borderRadius: 2,
      borderColor: "divider",
      backgroundColor: "#FFFFFF",
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "primary.main",
          backgroundColor: "#EAF2FF",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", fontWeight: 700, textTransform: "uppercase" }}
        >
          {label}
        </Typography>
        <Typography
          variant="body1"
          sx={{ mt: 0.5, fontWeight: 600 }}
          color={
            value === null || value === "Non spécifié"
              ? "#FF0000"
              : "text.primary"
          }
        >
          {value || "Non spécifié"}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const AmiInfoResume = ({ ami_detail, loading }) => {
  const optionLabel =
    DESC_DETAIL.find((item) => item.value === ami_detail?.desc)?.label ||
    "Non spécifié";
  const diplomeLabel =
    DIPLOMES.find((diplome) => diplome.id_diplome === ami_detail?.id_min_diplom)
      ?.label || "Non spécifié";
  const isClosed = ami_detail?.cloture === "1";
  const publicationDate = [
    formatDate(ami_detail?.date_debut_publication),
    formatDate(ami_detail?.date_fin_publication),
  ]
    .filter(Boolean)
    .join(" au ");

  if (loading) {
    return (
      <Grid item xs={12}>
        <Paper sx={{ p: 3, borderRadius: 2 }} variant="outlined">
          <Skeleton variant="text" width="35%" height={36} />
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <Grid item xs={12} md={6} key={item}>
                <Skeleton
                  variant="rectangular"
                  height={86}
                  sx={{ borderRadius: 2 }}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          background: "linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1.5}
        >
          <Box>
            <Typography variant="overline" color="primary" fontWeight={700}>
              Aperçu de l'offre
            </Typography>
            <Typography variant="h5" fontWeight={800}>
              Résumé de l'AMI
            </Typography>
          </Box>
          <Box display={"flex"} sx={{ gap: 1, flexWrap: "wrap" }}>
            <Chip
              label={
                "Visible pour " +
                formatPublicationDuration(
                  ami_detail?.date_debut_publication,
                  ami_detail?.date_fin_publication,
                )
              }
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 700 }}
            />
            <Chip
              label={isClosed ? "Clôturé" : "Ouvert"}
              color={isClosed ? "default" : "success"}
              variant={isClosed ? "outlined" : "filled"}
              sx={{ fontWeight: 700 }}
            />
          </Box>
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <InfoCard
              icon={<DescriptionOutlinedIcon fontSize="small" />}
              label="Référence"
              value={ami_detail?.description}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoCard
              icon={<InfoOutlinedIcon fontSize="small" />}
              label="Libellé de l'AMI"
              value={ami_detail?.libelle_type}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoCard
              icon={<CalendarTodayOutlinedIcon fontSize="small" />}
              label="Période de publication"
              value={publicationDate}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoCard
              icon={<CategoryOutlinedIcon fontSize="small" />}
              label="Type d'AMI"
              value={ami_detail?.type}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoCard
              icon={<InfoOutlinedIcon fontSize="small" />}
              label="Option"
              value={optionLabel}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoCard
              icon={<SchoolOutlinedIcon fontSize="small" />}
              label="Diplôme minimum requis"
              value={diplomeLabel}
            />
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
};

export default AmiInfoResume;
